// Story 153 / MIC-4291, CU10-CU11 (RN51-RN56, V10-V12, INV-8): resolves, persists and resets the
// order and the collapsed state of the four Planner accordion blocks, per authenticated user.
//
// The frozen table-tune key is "PlannerBlocks" (contract §4). Each field re-interprets the existing
// `UserTableTuneFieldDto` as: `name` = block key, `order` = 0-based position, `show` = expanded.
//
// Each block's `key` is its identity for `SmartSortableContainer` (React key and dnd-kit id) and
// `position` is its 0-based order (`itemOrder`). They MUST stay separate: an id that changed with the
// position would change the React keys on every drop and remount all four blocks -- Gantt, ribbon
// and tables re-created and re-fetched after each drag. A drag only re-assigns `position` (S114).
import { useCallback, useEffect, useRef, useState } from 'react'
import { apiGet, apiPost } from '../generic/Api_Functions'

export const PLANNER_BLOCK_CATALOG = ['TIMELINE', 'LOAD_CAPACITY', 'DETAILS_TABLE', 'PLANNING_TOOLS']

// RN52: default order, with the first two expanded and the last two collapsed.
const DEFAULT_EXPANDED_BLOCKS = {
  TIMELINE: true,
  LOAD_CAPACITY: true,
  DETAILS_TABLE: false,
  PLANNING_TOOLS: false
}

const TABLE_TUNE_KEY = 'PlannerBlocks'

/**
 * RN53: a block key that is no longer part of the catalog is dropped silently. A catalog key that is
 * missing from the saved preference is inserted right after the last catalog key that precedes it AND
 * is already present in the working list -- so the saved relative order of the rest never moves.
 *
 * @param {Array<{name: string, show: boolean, order: number}>} savedFields
 * @returns {Array<{key: string, expanded: boolean}>} resolved order, not yet re-indexed (see `normalizeBlocks`)
 */
export const mergeWithCatalog = (savedFields = []) => {
  const knownFields = (savedFields || [])
    .filter((field) => PLANNER_BLOCK_CATALOG.includes(field.name))
    .sort((a, b) => a.order - b.order)

  const orderedKeys = knownFields.map((field) => field.name)

  PLANNER_BLOCK_CATALOG.forEach((catalogKey, catalogIndex) => {
    if (orderedKeys.includes(catalogKey)) {
      return
    }
    const insertIndex = PLANNER_BLOCK_CATALOG
      .slice(0, catalogIndex)
      .filter((precedingKey) => orderedKeys.includes(precedingKey))
      .length
    orderedKeys.splice(insertIndex, 0, catalogKey)
  })

  return orderedKeys.map((key) => {
    const savedField = knownFields.find((field) => field.name === key)
    return {
      key,
      expanded: savedField ? Boolean(savedField.show) : DEFAULT_EXPANDED_BLOCKS[key]
    }
  })
}

/**
 * V11: positions are always `0..n-1`, without gaps or duplicates (S114).
 *
 * @param {Array<{key: string, expanded: boolean}>} blocks
 * @returns {Array<{key: string, position: number, expanded: boolean}>}
 */
export const normalizeBlocks = (blocks) => {
  const seenKeys = new Set()
  return blocks
    .filter((block) => {
      if (seenKeys.has(block.key)) {
        return false
      }
      seenKeys.add(block.key)
      return true
    })
    .map((block, index) => ({ key: block.key, position: index, expanded: block.expanded }))
}

const buildDefaultItems = () => normalizeBlocks(mergeWithCatalog([]))

const itemsToFields = (items) => items.map((item, index) => ({
  name: item.key,
  show: item.expanded,
  order: index
}))

// S106: Sun answers a non-successful response when the user has no `PlannerBlocks` row yet
// (`UserTableTuneRest`, contract §4) -- not a 200 with empty content. Any failure of this read, and
// any success whose `fields` is empty, is absorbed here as "no preference" and defaults apply, with
// no visible error. This mirrors, byte for byte, the catch branch of `SmartMateriaUITable`.
const readSavedPreference = async () => {
  try {
    const response = await apiGet('user/tabletune/' + TABLE_TUNE_KEY)
    if (response && Array.isArray(response.fields) && response.fields.length > 0) {
      return { id: response.id ?? null, fields: response.fields }
    }
    return { id: response?.id ?? null, fields: [] }
  } catch (error) {
    return { id: null, fields: [] }
  }
}

const usePlannerBlocksLayout = ({ onSaveError } = {}) => {
  const [items, setItems] = useState(buildDefaultItems)
  const [isLoaded, setIsLoaded] = useState(false)
  const itemsRef = useRef(items)
  itemsRef.current = items

  const rowIdRef = useRef(null)
  const saveInFlightRef = useRef(false)
  // S115: only one write in flight per user. A change made while a save is running just replaces the
  // pending payload -- the loop below always ends up persisting the latest state, never two writes
  // with `id: null` for the same user.
  const pendingFieldsRef = useRef(null)

  const runSaveLoop = useCallback(async () => {
    saveInFlightRef.current = true
    while (pendingFieldsRef.current !== null) {
      const fieldsToSend = pendingFieldsRef.current
      pendingFieldsRef.current = null
      try {
        const response = await apiPost('user/tabletune', {
          id: rowIdRef.current,
          tableKey: TABLE_TUNE_KEY,
          fields: fieldsToSend
        })
        rowIdRef.current = response?.id ?? rowIdRef.current
      } catch (error) {
        // CU10/A4: the view keeps the order the user just left -- it is never reverted here. The
        // next order or collapse change (not this same failed one) will try to save again.
        if (onSaveError) {
          onSaveError(error)
        }
      }
    }
    saveInFlightRef.current = false
  }, [onSaveError])

  const schedulePersist = useCallback((fields) => {
    pendingFieldsRef.current = fields
    if (!saveInFlightRef.current) {
      runSaveLoop()
    }
  }, [runSaveLoop])

  useEffect(() => {
    let mounted = true
    readSavedPreference().then(({ id, fields }) => {
      if (!mounted) {
        return
      }
      rowIdRef.current = id
      setItems(normalizeBlocks(mergeWithCatalog(fields)))
      setIsLoaded(true)
    })
    return () => {
      mounted = false
    }
  }, [])

  // S107/S114: a drag reorders the array; every block keeps its key and expanded state and only its
  // `position` is reassigned. That is what makes the reorder stick once `SmartSortableContainer`
  // re-sorts `itemList` by position again on the next render.
  const onDragEnd = useCallback((reorderedItems) => {
    const nextItems = reorderedItems.map((item, index) => ({ ...item, position: index }))
    setItems(nextItems)
    schedulePersist(itemsToFields(nextItems))
  }, [schedulePersist])

  // S108: collapsing/expanding a block persists the full set, only flipping that one block's `show`.
  // Guarded against no-op calls: `DefaultAccordion.onExpandedStatusChange` fires once on every mount
  // with whatever `defaultExpanded` it was given -- i.e. with the value this hook already holds -- so
  // without this guard a consumer whose own `onExpandedStatusChange` closure is not perfectly stable
  // across renders would keep "confirming" the same state forever (setItems -> re-render -> confirm).
  const onBlockExpandedChange = useCallback((key, expanded) => {
    const current = itemsRef.current.find((item) => item.key === key)
    if (!current || current.expanded === expanded) {
      return
    }
    const nextItems = itemsRef.current.map((item) => (item.key === key ? { ...item, expanded } : item))
    setItems(nextItems)
    schedulePersist(itemsToFields(nextItems))
  }, [schedulePersist])

  // CU11/RN54: reset is a POST with `fields: []` and the row id read at load time. There is no
  // DELETE, none is added, and no other user preference is touched.
  const resetToDefault = useCallback(() => {
    setItems(buildDefaultItems())
    schedulePersist([])
  }, [schedulePersist])

  return { items, isLoaded, onDragEnd, onBlockExpandedChange, resetToDefault }
}

export default usePlannerBlocksLayout
